import {
  ArrowRightLeft,
  CheckCircle2,
  Lock,
  MessageSquare,
  Paperclip,
  RotateCcw,
  TimerOff,
  TicketPlus,
  UserCheck,
  XCircle,
} from 'lucide-react';
import {
  TICKET_EVENT_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_STATUS_LABELS,
  type TicketEvent,
  type TicketEventType,
} from '@electrodavisan/shared';
import { formatDateTime } from '../../lib/format';
import { SkeletonList } from '../../components/ui';

const ICONS: Record<TicketEventType, JSX.Element> = {
  CREATED: <TicketPlus className="h-3.5 w-3.5" />,
  ASSIGNED: <UserCheck className="h-3.5 w-3.5" />,
  UNASSIGNED: <UserCheck className="h-3.5 w-3.5" />,
  STATUS_CHANGED: <ArrowRightLeft className="h-3.5 w-3.5" />,
  PRIORITY_CHANGED: <ArrowRightLeft className="h-3.5 w-3.5" />,
  COMMENT_PUBLIC: <MessageSquare className="h-3.5 w-3.5" />,
  COMMENT_INTERNAL: <Lock className="h-3.5 w-3.5" />,
  ATTACHMENT_ADDED: <Paperclip className="h-3.5 w-3.5" />,
  RESOLVED: <CheckCircle2 className="h-3.5 w-3.5" />,
  CLOSED: <XCircle className="h-3.5 w-3.5" />,
  // Reloj y no aspa: lo que cerro el ticket fue el tiempo, no una decision.
  AUTO_CLOSED: <TimerOff className="h-3.5 w-3.5" />,
  REOPENED: <RotateCcw className="h-3.5 w-3.5" />,
};

/** Traduce los valores tecnicos (OPEN, HIGH...) a etiquetas en espanol. */
function translateValue(value: string | null): string | null {
  if (!value) return null;
  return (
    TICKET_STATUS_LABELS[value as keyof typeof TICKET_STATUS_LABELS] ??
    TICKET_PRIORITY_LABELS[value as keyof typeof TICKET_PRIORITY_LABELS] ??
    value
  );
}

export function TicketTimeline({
  events,
  isLoading,
}: {
  events: TicketEvent[] | undefined;
  isLoading: boolean;
}): JSX.Element {
  if (isLoading) return <SkeletonList rows={3} />;

  if (!events || events.length === 0) {
    return <p className="text-sm text-text-muted">Sin actividad registrada.</p>;
  }

  return (
    <ol className="relative space-y-5 border-l border-border pl-6">
      {events.map((event) => {
        const from = translateValue(event.fromValue);
        const to = translateValue(event.toValue);
        const isInternal = event.eventType === 'COMMENT_INTERNAL';

        return (
          <li key={event.id} className="relative">
            <span
              aria-hidden
              className={[
                'absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full border',
                isInternal
                  ? 'border-warning/40 bg-warning-soft text-warning'
                  : 'border-border bg-surface-secondary text-text-muted',
              ].join(' ')}
            >
              {ICONS[event.eventType] ?? <MessageSquare className="h-3.5 w-3.5" />}
            </span>

            <p className="text-sm font-medium text-text-primary">
              {TICKET_EVENT_LABELS[event.eventType] ?? event.eventType}
              {from && to ? (
                <span className="font-normal text-text-secondary">
                  {' '}
                  — {from} → {to}
                </span>
              ) : to ? (
                <span className="font-normal text-text-secondary"> — {to}</span>
              ) : null}
            </p>

            {event.message ? (
              <p className="mt-0.5 text-[13px] text-text-secondary">{event.message}</p>
            ) : null}

            <p className="mt-1 text-[12px] text-text-muted">
              {event.actorName ?? 'Sistema'} · {formatDateTime(event.createdAt)}
            </p>
          </li>
        );
      })}
    </ol>
  );
}
